import { ColDef } from "ag-grid-community";

import {
  bulkCreateExportP2PIn,
  bulkCreateExportP2POut,
  ExportP2PInFileAction,
  ExportP2PInReversalAction,
  ExportP2POutFileAction,
  ExportP2POutReversalAction,
} from "actions";

import { AgGridTables } from "constants/aggrid";
import {
  destinationDisplayColumnDefinition,
  NlisFileColumnDefinitions,
  NlisFileColId,
  NlisFileRow,
} from "constants/columnDefinitions/nlisFile";
import { Column as AgGridColumn } from "constants/columns";
import { ApiModel } from "constants/loading";

import { withNestedField } from "lib/agGrid";
import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";
import { pluralize } from "lib/pluralize";

import {
  getScansForExportP2PIn,
  getScansForExportP2POut,
  selectExportP2PInTransactions,
  selectExportP2POutTransactions,
} from "selectors";

// Only the fields this file reads off a scanList() row, not its full shape.
interface P2PLegScanRow {
  scan: { EID: string };
  consignment?: { id: string } | null;
  saleLot?: { id: string } | null;
  buyer?: { id: string } | null;
}

export interface BannerCtx {
  transferableScans: P2PLegScanRow[];
  consignments: Record<string, unknown>;
  saleLots: Record<string, unknown>;
}

export interface P2PLegConfig {
  apiModel: { files: string; reversals: string };
  agGrid: {
    table: string;
    previewTable: string;
    // Overrides NLISFileGrid's default columns - the Out leg uses this to
    // swap Buyer for Destination.
    transactionColumnDefs?: ColDef<NlisFileRow>[];
  };
  actions: {
    fileAction: { request: () => unknown };
    reversalAction: { request: () => unknown };
    bulkCreate: (eids: string[]) => unknown;
  };
  transferableScansSelector: (state: unknown) => P2PLegScanRow[];
  // The file rows shown in the grid, not the scan rows above.
  transactionsSelector: (state: unknown) => NlisFileRow[];
  ui: {
    dialogTitle: (saleyardPic: string | null) => string;
    confirmationColumnDefs: ColDef<P2PLegScanRow>[];
    bannerText: (ctx: BannerCtx) => string;
    allDoneMessage: string;
  };
}

const countDistinct = (
  rows: P2PLegScanRow[],
  accessor: (row: P2PLegScanRow) => unknown,
): number => new Set(rows.map(accessor).filter(Boolean)).size;

const confirmationColumnDefsPrefix = [
  {
    headerName: "EID",
    field: "scan.EID",
    minWidth: 90,
  },
  withNestedField(AgGridColumn.PEN, "saleLot"),
];

const confirmationColumnDefsIn = [
  ...confirmationColumnDefsPrefix,
  withNestedField(AgGridColumn.VENDOR_NAME, "saleLot"),
  BaseColumnDefinitions[AgGridColumn.NVD],
  BaseColumnDefinitions[AgGridColumn.VENDOR_PIC],
];

const confirmationColumnDefsOut = [
  ...confirmationColumnDefsPrefix,
  withNestedField(AgGridColumn.BUYER_NAME, "saleLot"),
  withNestedField(AgGridColumn.DESTINATION_PIC, "saleLot"),
  BaseColumnDefinitions[AgGridColumn.NVD],
];

export const exportP2PInLegConfig: P2PLegConfig = {
  apiModel: {
    files: ApiModel.NLIS_EXPORT_P2P_IN_FILES,
    reversals: ApiModel.NLIS_EXPORT_P2P_IN_REVERSALS,
  },
  agGrid: {
    table: AgGridTables.NLIS_EXPORT_P2P_IN,
    previewTable: AgGridTables.NLIS_EXPORT_P2P_IN_PREVIEW,
  },
  actions: {
    fileAction: ExportP2PInFileAction,
    reversalAction: ExportP2PInReversalAction,
    bulkCreate: bulkCreateExportP2PIn,
  },
  // Cast: the underlying selectors are untyped .js, so reselect infers
  // the wrong row shape here even though these fields exist at runtime.
  transferableScansSelector: getScansForExportP2PIn as unknown as (
    state: unknown,
  ) => P2PLegScanRow[],
  transactionsSelector: selectExportP2PInTransactions as unknown as (
    state: unknown,
  ) => NlisFileRow[],
  ui: {
    dialogTitle: saleyardPic =>
      `Are you sure you wish to P2P In the following EIDs to the Saleyard PIC (${
        saleyardPic || "not set"
      })?`,
    confirmationColumnDefs: confirmationColumnDefsIn,
    // A single "Transfer Now" click can span multiple consignments, so
    // this counts consignments like Take, not just scans.
    bannerText: ({ transferableScans, consignments }) => {
      const numConsignments = countDistinct(
        transferableScans,
        row => row.consignment?.id,
      );
      const totalConsignments = Object.keys(consignments).length;
      return `You currently have ${
        transferableScans.length
      } Transferable ${pluralize(
        "EID",
        transferableScans.length,
      )} in ${numConsignments} (of ${totalConsignments}) ${pluralize(
        "Consignment",
        numConsignments,
      )}`;
    },
    allDoneMessage: "All available Consignments have been P2P transferred In.",
  },
};

// Out's counterparty is a destination property, not a real Buyer, so
// show Destination instead.
const transactionColumnDefsOut = Object.values(NlisFileColumnDefinitions).map(
  colDef =>
    colDef.colId === NlisFileColId.BUYER_DISPLAY
      ? destinationDisplayColumnDefinition
      : colDef,
);

export const exportP2POutLegConfig: P2PLegConfig = {
  apiModel: {
    files: ApiModel.NLIS_EXPORT_P2P_OUT_FILES,
    reversals: ApiModel.NLIS_EXPORT_P2P_OUT_REVERSALS,
  },
  agGrid: {
    table: AgGridTables.NLIS_EXPORT_P2P_OUT,
    previewTable: AgGridTables.NLIS_EXPORT_P2P_OUT_PREVIEW,
    transactionColumnDefs: transactionColumnDefsOut,
  },
  actions: {
    fileAction: ExportP2POutFileAction,
    reversalAction: ExportP2POutReversalAction,
    bulkCreate: bulkCreateExportP2POut,
  },
  transferableScansSelector: getScansForExportP2POut as unknown as (
    state: unknown,
  ) => P2PLegScanRow[],
  transactionsSelector: selectExportP2POutTransactions as unknown as (
    state: unknown,
  ) => NlisFileRow[],
  ui: {
    dialogTitle: saleyardPic =>
      `Are you sure you wish to P2P Out the following EIDs from the Saleyard PIC (${
        saleyardPic || "not set"
      })?`,
    confirmationColumnDefs: confirmationColumnDefsOut,
    // A single "Transfer Now" click can span multiple sale lots and
    // buyers, so this counts both like Sell does.
    bannerText: ({ transferableScans, saleLots }) => {
      const saleLotCount = countDistinct(
        transferableScans,
        row => row.saleLot?.id,
      );
      const totalSaleLots = Object.keys(saleLots).length;
      const numBuyers = countDistinct(transferableScans, row => row.buyer?.id);
      return `You currently have ${
        transferableScans.length
      } Transferable ${pluralize(
        "EID",
        transferableScans.length,
      )} in ${saleLotCount} (of ${totalSaleLots}) Sale ${pluralize(
        "Lot",
        saleLotCount,
      )} for ${numBuyers} ${pluralize("Buyer", numBuyers)}`;
    },
    allDoneMessage: "All available Sale Lots have been P2P transferred Out.",
  },
};
